import React, { useRef, useState } from "react";
import { StyleConfigModel } from "@/pages/Messaging/types";
import { IconTrash } from "@velaro/velaro-component-lib";
import IconTrash1 from "@/icons/IconTrash1";

interface Props {
  setStageLogoFile: (file: File | null) => void;
  update: (updates: Partial<StyleConfigModel>) => void;
  workingStyleConfig: StyleConfigModel;
  field: keyof Pick<StyleConfigModel, "logoUrl" | "queueGraphicUrl" | "headerBackgroundImageUrl" | "launcherIconUrl" | "contentWidgetImageUrl">;
  label?: string;
  accept?: string;
  maxSizeMb?: number;
  maxWidth?: number;
  maxHeight?: number;
  sizeGuidance?: string;
  previewClassName?: string;
}

export default function LogoUpload(props: Props) {
  const {
    accept = "image/png,image/svg+xml",
    maxSizeMb = 5,
    maxWidth,
    maxHeight,
    sizeGuidance = "Up to 5MB \u2022 Supports PNG, SVG \u2022 42px \u00d7 200px",
    previewClassName = "h-10 w-10 object-contain"
  } = props;

  const fileInput = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = (file: File) => {
    setError(null);

    if (file.size > maxSizeMb * 1024 * 1024) {
      setError(`File exceeds ${maxSizeMb}MB limit. Please choose a smaller image.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result?.toString();
      if (!dataUrl) return;

      if (maxWidth || maxHeight) {
        const img = new Image();
        img.onload = () => {
          if (
            (maxWidth && img.naturalWidth > maxWidth) ||
            (maxHeight && img.naturalHeight > maxHeight)
          ) {
            setError(
              `Image must be ${maxWidth ?? "any"}\u00d7${maxHeight ?? "any"}px or smaller. Yours is ${img.naturalWidth}\u00d7${img.naturalHeight}px.`
            );
            return;
          }
          props.update({ [props.field]: dataUrl });
          props.setStageLogoFile(file);
          setFileName(file.name);
        };
        img.src = dataUrl;
      } else {
        props.update({ [props.field]: dataUrl });
        props.setStageLogoFile(file);
        setFileName(file.name);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleBrowse = () => {
    fileInput.current?.click();
  };

  const handleRemove = () => {
    props.update({ [props.field]: "" });
    props.setStageLogoFile(null);
    setFileName(null);
    setError(null);
  };

  const url = props.workingStyleConfig[props.field];

  return (
    <div>
      <div className="text-sm font-medium mb-2">{props.label}</div>

      {url ? (
        <div className="border rounded-md p-4 bg-slate-50 flex items-center justify-between mb-2">
          <div className="flex items-center space-x-4 overflow-hidden">
            <img
              src={url}
              alt={`${props.label} preview`}
              className={previewClassName}
            />
            <span className="truncate text-sm text-slate-500">
              {fileName ?? `Uploaded ${props.label?.toLowerCase()}`}
            </span>
          </div>
          <button
            onClick={handleRemove}
            className="text-slate-500 hover:text-red-600 transition"
            aria-label="Remove file"
          >
            <IconTrash className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <label
          onClick={handleBrowse}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`w-full flex items-center justify-center border-2 border-dashed rounded-md px-4 py-6 text-sm text-slate-500 cursor-pointer transition ${
            dragOver ? "bg-blue-50 border-cornflower-blue-500" : "bg-slate-50"
          }`}
        >
          Drag & Drop your file or{" "}
          <span className="underline ml-1">Browse</span>
        </label>
      )}

      <input
        type="file"
        accept={accept}
        ref={fileInput}
        className="hidden"
        onChange={(e) => {
          const file = e.currentTarget.files?.[0];
          if (file) handleFile(file);
          e.currentTarget.value = "";
        }}
      />

      {error ? (
        <div className="mt-2 text-xs font-medium text-red-600">{error}</div>
      ) : (
        <div className="mt-2 text-xs font-normal text-slate-500">{sizeGuidance}</div>
      )}
    </div>
  );
}
